export interface ParsedPackaging {
  packagingSize: number | null;
  packagingType: string;
  packagingDescription: string;
}

/**
 * Parse the COND field from Algerian drug nomenclature into structured packaging info.
 *
 * Examples:
 *   "B/20"           → { packagingSize: 20, packagingType: "boite", packagingDescription: "Boite de 20" }
 *   "B/06AMP. DE 10ML" → { packagingSize: 6, packagingType: "boite", packagingDescription: "Boite de 6 (amp. de 10ml)" }
 *   "FL/125ML"       → { packagingSize: 1, packagingType: "flacon", packagingDescription: "Flacon 125ml" }
 *   "TUBE/30G"       → { packagingSize: 1, packagingType: "tube", packagingDescription: "Tube 30g" }
 */
export function parseCondField(cond: string): ParsedPackaging | null {
  if (!cond || !cond.trim()) return null;

  const raw = cond.trim().toUpperCase();

  // B/20, B/06AMP. DE 10ML, B/10X5ML, etc.
  const boiteMatch = raw.match(/^B\/(\d+)\s*(.*)/);
  if (boiteMatch) {
    const size = parseInt(boiteMatch[1]!, 10);
    const extra = boiteMatch[2]?.trim();
    const desc = extra
      ? `Boite de ${size} (${extra.toLowerCase()})`
      : `Boite de ${size}`;
    return { packagingSize: size, packagingType: 'boite', packagingDescription: desc };
  }

  // FL/125ML, FL/60ML, FLACON/...
  const flaconMatch = raw.match(/^(?:FL|FLACON)\/(.*)/);
  if (flaconMatch) {
    const detail = flaconMatch[1]?.trim() || '';
    return {
      packagingSize: 1,
      packagingType: 'flacon',
      packagingDescription: `Flacon ${detail.toLowerCase()}`.trim(),
    };
  }

  // TUBE/30G, TUBE/...
  const tubeMatch = raw.match(/^TUBE\/(.*)/);
  if (tubeMatch) {
    const detail = tubeMatch[1]?.trim() || '';
    return {
      packagingSize: 1,
      packagingType: 'tube',
      packagingDescription: `Tube ${detail.toLowerCase()}`.trim(),
    };
  }

  // Fallback: unknown format
  return {
    packagingSize: null,
    packagingType: 'autre',
    packagingDescription: cond.trim(),
  };
}

// Liquid/topical forms where quantity=1 means "1 bottle/tube" — no box hint needed
const LIQUID_FORMS = ['sirop', 'solution', 'suspension', 'aerosol', 'aérosol', 'spray',
  'inhal', 'creme', 'crème', 'gel', 'pommade', 'goutte', 'collyre', 'topique'];

const UNIT_ABBREVS: Record<string, string> = {
  'comprimé': 'cp',
  'comprime': 'cp',
  'comp': 'cp',
  'gélule': 'gél',
  'gelule': 'gél',
  'capsule': 'gél',
  'suppositoire': 'suppo',
  'sachet': 'sachet',
  'poudre': 'sachet',
  'ampoule': 'amp',
  'amp': 'amp',
  'patch': 'patch',
};

function getUnitAbbrev(dosageForm?: string): string {
  if (!dosageForm) return 'unité';
  const lower = dosageForm.toLowerCase();
  for (const [key, abbrev] of Object.entries(UNIT_ABBREVS)) {
    if (lower.includes(key)) return abbrev;
  }
  return 'unité';
}

/**
 * Format a packaging hint showing how many boxes + loose units are needed.
 *
 * Examples:
 *   (21, 20, "Comprimé") → "1 boite + 1 cp"
 *   (40, 20, "Comprimé") → "2 boites"
 *   (5, 20, "Comprimé")  → "5 cp"
 *
 * Returns null for liquid forms or when packagingSize is missing.
 */
export function formatPackagingHint(
  quantity: number,
  packagingSize?: number,
  dosageForm?: string,
): string | null {
  if (!packagingSize || packagingSize <= 1) return null;
  if (!quantity || quantity <= 0) return null;

  // Skip liquid/topical forms
  if (dosageForm) {
    const lower = dosageForm.toLowerCase();
    if (LIQUID_FORMS.some((f) => lower.includes(f))) return null;
  }

  const boxes = Math.floor(quantity / packagingSize);
  const remainder = quantity % packagingSize;
  const unit = getUnitAbbrev(dosageForm);
  const pluralUnit = unit.endsWith('s') ? unit : unit + 's';

  const parts: string[] = [];
  if (boxes > 0) {
    parts.push(`${boxes} boite${boxes > 1 ? 's' : ''}`);
  }
  if (remainder > 0) {
    parts.push(`${remainder} ${remainder > 1 ? pluralUnit : unit}`);
  }

  return parts.join(' + ') || null;
}
